import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_IMAGE = '/og-image.png';
const SITE_NAME = 'ViajeJunto';
const DEFAULT_DESCRIPTION = 'Organize viagens em grupo sem estresse. Divida despesas, vote em decisões, compartilhe roteiros e coordene caronas.';

/**
 * SEO Component for managing page meta tags
 * @param {string} title - Page title
 * @param {string} description - Page description
 * @param {string} image - Open Graph image URL
 * @param {string} url - Canonical URL
 * @param {string} type - Open Graph type (website, article, etc.)
 * @param {object} tripData - Optional trip data for trip-specific pages
 */
export const SEO = ({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  tripData = null,
  noIndex = false
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Planeje Viagens em Grupo`;
  
  // If trip data is provided, use it for rich snippets
  const structuredData = tripData ? {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": tripData.name,
    "description": tripData.description || `Viagem para ${tripData.destination}`,
    "startDate": tripData.start_date,
    "endDate": tripData.end_date,
    "location": {
      "@type": "Place",
      "name": tripData.destination
    },
    "organizer": {
      "@type": "Person",
      "name": tripData.organizer_name
    }
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {url && <link rel="canonical" href={url} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="pt_BR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {url && <meta name="twitter:url" content={url} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Structured Data for Trips */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Predefined SEO configurations for common pages
 */
export const SEOPages = {
  landing: {
    title: null,
    description: 'Organize viagens em grupo sem estresse. Divida despesas, vote em decisões, compartilhe roteiros e coordene caronas. Tudo em um só lugar. Grátis!'
  },
  dashboard: {
    title: 'Minhas Viagens',
    description: 'Gerencie suas viagens em grupo. Veja roteiros, acompanhe pagamentos e coordene com seus amigos.',
    noIndex: true
  },
  createTrip: {
    title: 'Criar Nova Viagem',
    description: 'Crie uma nova viagem em grupo e convide seus amigos. Configure roteiro, despesas e muito mais.',
    noIndex: true
  },
  tripDetail: (tripName, destination) => ({
    title: tripName,
    description: `Detalhes da viagem para ${destination}. Roteiro, pagamentos, votações e mais.`,
    noIndex: true
  }),
  payments: (tripName) => ({
    title: `Pagamentos - ${tripName}`,
    description: `Gerencie os pagamentos e divisão de despesas da viagem ${tripName}.`,
    noIndex: true
  }),
  itinerary: (tripName) => ({
    title: `Roteiro - ${tripName}`,
    description: `Confira o roteiro completo da viagem ${tripName}. Atividades, horários e locais.`,
    noIndex: true
  }),
  votes: (tripName) => ({
    title: `Votações - ${tripName}`,
    description: `Vote nas decisões do grupo para a viagem ${tripName}.`,
    noIndex: true
  })
};

export default SEO;
